import React from "react";
import { motion } from "framer-motion";
import { Award, Trophy } from "lucide-react";
import { SectionHeader } from "@/components/sections/Services";
import { HOME } from "@/constants/testIds";

const highlights = [
  "Founded in 2024 with a focus on innovation",
  "Serving 100+ publishers globally",
  "Processing 3B+ ad requests monthly",
  "Award-winning customer support",
];

const recognitions = [
  { icon: Trophy, text: "Best Debut Ad Tech Platform 2024" },
  { icon: Award, text: "Top Publisher Partner Award" },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid={HOME.aboutSection}
      className="relative py-32"
    >
      <div className="container-x">
        <SectionHeader
          eyebrow="About MonetiseUP"
          title={
            <>
              Innovating
              <br />
              <span className="aurora-text">Digital Advertising</span>
            </>
          }
        />

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6 text-white/70 leading-relaxed">
            <p>
              Founded with the vision to revolutionize digital advertising,
              MonetiseUP has grown to become a trusted partner for publishers
              and advertisers worldwide. Our commitment to innovation,
              transparency, and results drives everything we do.
            </p>
            <p>
              We believe in the power of data-driven advertising to create
              meaningful connections between brands and consumers. Our platform
              combines cutting-edge technology with human expertise to deliver
              exceptional results that exceed expectations.
            </p>

            <div className="grid md:grid-cols-2 gap-6 pt-6">
              <div className="tile p-6">
                <h4 className="font-display text-lg font-bold mb-2">
                  Our <span className="cyan-text">Mission</span>
                </h4>
                <p className="text-sm text-white/65">
                  To empower publishers and advertisers with innovative
                  technology that drives sustainable growth and meaningful
                  engagement.
                </p>
              </div>
              <div className="tile p-6">
                <h4 className="font-display text-lg font-bold mb-2">
                  Our <span className="cyan-text">Vision</span>
                </h4>
                <p className="text-sm text-white/65">
                  To be the leading platform that transforms how digital
                  advertising creates value for all stakeholders in the
                  ecosystem.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="glass-strong rounded-3xl p-8"
            >
              <h4 className="font-display text-2xl font-bold mb-6">
                Company<span className="cyan-text"> Highlights</span>
              </h4>
              <ul className="space-y-4">
                {highlights.map((h, i) => (
                  <li
                    key={h}
                    className="flex items-start gap-4 pb-4 border-b border-white/5 last:border-0"
                  >
                    <span className="font-mono text-xs text-[#00F0FF]/70 mt-0.5">
                      0{i + 1}
                    </span>
                    <span className="text-sm text-white/80">{h}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="glass-strong rounded-3xl p-8"
            >
              <h4 className="font-display text-2xl font-bold mb-6">
                Industry<span className="cyan-text"> Recognition</span>
              </h4>
              <div className="space-y-3">
                {recognitions.map((r) => (
                  <div
                    key={r.text}
                    className="flex items-center gap-4 p-4 rounded-xl border border-white/5 hover:border-[#00F0FF]/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0047FF]/40 to-[#00F0FF]/20 grid place-items-center">
                      <r.icon className="w-5 h-5 text-[#00F0FF]" />
                    </div>
                    <span className="text-sm text-white/85 font-medium">
                      {r.text}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
